// Gevel & crepi: de progressieve ladder volgens prijslijst A of B (SPEC_wijzigingen 1). De gebruikte
// variant staat altijd zichtbaar; zolang de keuze niet bevestigd is kan de calculatie niet op
// goedgekeurd.
import { CREPI_DEEL_LABEL, GEVEL_VARIANT_LABEL, type GevelConfig, type GevelResultaat, type GevelVariant } from '@recotex/shared';
import { fmtEUR, fmtNum, fmtDateTime } from '../lib/format';

export function GevelBlock({
  gevel,
  resultaat,
  isGevel,
  standaardVariant,
  editable,
  onKies,
  onVerversen,
}: {
  gevel: GevelConfig | null;
  resultaat: GevelResultaat | null;
  /** Bevat de calculatie crepi of steenstrips? */
  isGevel: boolean;
  standaardVariant: GevelVariant;
  editable: boolean;
  /** Open de pop-up om de variant te kiezen/bevestigen. */
  onKies: () => void;
  /** Laad de actuele prijslijstversie (opent ook de pop-up). */
  onVerversen: () => void;
}) {
  if (!isGevel) return null;

  if (!gevel) {
    // Calculatie van vóór de progressieve ladder (open punt 10.4: datum van de calculatie).
    return (
      <div className="card mb-5 border-l-4 border-warning normal-case tracking-normal">
        <h3 className="text-lg font-bold mb-1">Gevel &amp; crepi</h3>
        <p className="text-sm text-secondary">
          Deze calculatie rekent nog met de vaste crepiprijs van de oude prijslijst. Ververs de prijslijst om de progressieve
          schijvenladder (prijslijst A of B) toe te passen.
        </p>
        {editable && <button className="btn-ghost btn-sm mt-3" onClick={onVerversen}>↻ Prijslijst verversen</button>}
      </div>
    );
  }

  const r = resultaat;
  const afwijking = gevel.variant !== standaardVariant;
  return (
    <div className={`card mb-5 border-l-4 ${gevel.bevestigd ? 'border-primary' : 'border-warning'} normal-case tracking-normal`}>
      <div className="flex justify-between items-start gap-3 flex-wrap mb-3">
        <div>
          <h3 className="text-lg font-bold">Gevel &amp; crepi — prijslijst {GEVEL_VARIANT_LABEL[gevel.variant]}</h3>
          <div className="text-xs text-secondary mt-0.5">
            Versie {gevel.versie}{gevel.geldigVanaf ? ` (geldig vanaf ${gevel.geldigVanaf})` : ''} · standaard van de onderaannemer: {standaardVariant}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {afwijking && <span className="chip-warning" title="Afwijkend van de standaard van de onderaannemer">afwijkend</span>}
          {gevel.bevestigd
            ? <span className="chip-success" title={gevel.bevestigdOp ? `Bevestigd op ${fmtDateTime(gevel.bevestigdOp)}` : undefined}>✓ bevestigd</span>
            : <span className="chip-warning">nog niet bevestigd</span>}
          {editable && (
            <>
              <button className="btn-ghost btn-sm" onClick={onKies}>{gevel.bevestigd ? 'Wijzig A/B' : 'Prijslijst bevestigen'}</button>
              <button className="btn-ghost btn-sm" onClick={onVerversen} title="Actuele prijslijstversie laden en de ladder herrekenen">↻ Verversen</button>
            </>
          )}
        </div>
      </div>

      {r ? (
        <>
          <div className="text-sm text-secondary mb-2 tabular-nums">
            {fmtNum(r.metMeerwerk.m2Crepi)} m² crepi, waarvan {fmtNum(r.metMeerwerk.m2Met)} m² met isolatie (EPS/PIR) en {fmtNum(r.metMeerwerk.m2Zonder)} m² zonder.
            {' '}{CREPI_DEEL_LABEL[r.metMeerwerk.eerst]} vult de ladder eerst.
          </div>
          <table className="w-full">
            <thead>
              <tr className="text-xs uppercase tracking-allcaps border-b border-border">
                <th className="text-left px-3 py-2">Deel</th>
                <th className="text-left px-3 py-2 w-32">Schijf</th>
                <th className="text-right px-3 py-2 w-24">m²</th>
                <th className="text-right px-3 py-2 w-28">Tarief</th>
                <th className="text-right px-3 py-2 w-32">Bedrag</th>
              </tr>
            </thead>
            <tbody>
              {r.metMeerwerk.regels.map((x, i) => (
                <tr key={i} className="border-b border-lightgrey">
                  <td className="px-3 py-1.5 text-sm">{CREPI_DEEL_LABEL[x.deel]}</td>
                  <td className="px-3 py-1.5 text-sm">{x.schijf}</td>
                  <td className="px-3 py-1.5 text-sm text-right tabular-nums">{fmtNum(x.hoeveelheid)}</td>
                  <td className="px-3 py-1.5 text-sm text-right tabular-nums">{fmtEUR(x.prijs)}</td>
                  <td className="px-3 py-1.5 text-sm text-right tabular-nums font-bold">{fmtEUR(x.bedrag)}</td>
                </tr>
              ))}
              {r.steenstrips.m2ZonderVoeg + r.steenstrips.m2MeerwerkZonderVoeg > 0 && (
                <tr className="border-b border-lightgrey">
                  <td className="px-3 py-1.5 text-sm" colSpan={2}>Steenstrips 2 cm zonder voeg</td>
                  <td className="px-3 py-1.5 text-sm text-right tabular-nums">{fmtNum(r.steenstrips.m2ZonderVoeg + r.steenstrips.m2MeerwerkZonderVoeg)}</td>
                  <td className="px-3 py-1.5 text-sm text-right tabular-nums">{fmtEUR(gevel.steenstrips.zonderVoeg)}</td>
                  <td className="px-3 py-1.5 text-sm text-right tabular-nums font-bold">{fmtEUR((r.steenstrips.m2ZonderVoeg + r.steenstrips.m2MeerwerkZonderVoeg) * gevel.steenstrips.zonderVoeg)}</td>
                </tr>
              )}
              {r.steenstrips.m2MetVoeg + r.steenstrips.m2MeerwerkMetVoeg > 0 && (
                <tr className="border-b border-lightgrey">
                  <td className="px-3 py-1.5 text-sm" colSpan={2}>Steenstrips 2 cm met voeg</td>
                  <td className="px-3 py-1.5 text-sm text-right tabular-nums">{fmtNum(r.steenstrips.m2MetVoeg + r.steenstrips.m2MeerwerkMetVoeg)}</td>
                  <td className="px-3 py-1.5 text-sm text-right tabular-nums">{fmtEUR(gevel.steenstrips.metVoeg)}</td>
                  <td className="px-3 py-1.5 text-sm text-right tabular-nums font-bold">{fmtEUR((r.steenstrips.m2MetVoeg + r.steenstrips.m2MeerwerkMetVoeg) * gevel.steenstrips.metVoeg)}</td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={4} className="px-3 py-2 text-right text-sm">
                  In werkposten: <strong>{fmtEUR(r.totaalWerkposten)}</strong>
                  {r.totaalMeerwerk ? <> · in meerwerk (extra m² schuiven de schijven mee): <strong>{fmtEUR(r.totaalMeerwerk)}</strong></> : null}
                </td>
                <td className="px-3 py-2 text-right font-extrabold tabular-nums">{fmtEUR(r.totaalWerkposten + r.totaalMeerwerk)}</td>
              </tr>
            </tfoot>
          </table>
          <p className="text-xs text-secondary mt-2">
            De lijnen "plaatsen van crepi", "EPS-/PIR-gevelisolatie" en "steenstrips" in de werkposten worden door deze ladder geprijsd; de isolatie
            zit inbegrepen in crepi met isolatie.
          </p>
          {r.waarschuwingen.map((w) => <p key={w} className="text-sm text-warning mt-1">⚠ {w}</p>)}
        </>
      ) : null}
    </div>
  );
}
